"use client"

import { useState } from "react"
import { Badge } from "@/components/ui/badge"
import { ImagePromptForm } from "@/components/image-prompt-form"
import { ImageGallery } from "@/components/image-gallery"
import { Features } from "@/components/features"
import { Stats } from "@/components/stats"
import { Testimonials } from "@/components/testimonials"
import { FAQ } from "@/components/faq"
import { useLanguage } from "@/contexts/language-context"
import { Sparkles } from "lucide-react"

export default function Home() {
  const { t } = useLanguage()
  const [selectedPrompt, setSelectedPrompt] = useState("")

  const handleImageClick = (prompt: string) => {
    setSelectedPrompt(prompt)
    // 滚动到AI生成器部分
    const aiGeneratorSection = document.getElementById("ai-generator")
    if (aiGeneratorSection) {
      aiGeneratorSection.scrollIntoView({ behavior: "smooth" })
    }
  }

  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="w-full py-12 md:py-24 lg:py-32 bg-[#1a1310] border-b border-muted/20">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex flex-col items-center space-y-6 text-center">
            <div className="space-y-4">
              <h1 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl lg:text-6xl/none text-amber-500">
                {t("heroTitle")}
              </h1>
              <p className="mx-auto max-w-[700px] text-muted-foreground md:text-xl">{t("heroSubtitle")}</p>
            </div>

            {/* World First Free Banner */}
            <div className="flex items-center space-x-2 text-amber-400 text-lg font-medium">
              <Sparkles className="h-5 w-5" />
              <span>{t("worldFirstFree")}</span>
              <Sparkles className="h-5 w-5" />
            </div>

            {/* Feature Badges */}
            <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
              <Badge className="bg-amber-500 hover:bg-amber-600 text-black font-medium px-4 py-2 text-sm">
                {t("hundredPercentFree")}
              </Badge>
              <Badge className="bg-green-600 hover:bg-green-700 text-white font-medium px-4 py-2 text-sm">
                {t("poweredByFlux")}
              </Badge>
              <Badge className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 text-sm">
                {t("noLoginRequired")}
              </Badge>
              <Badge className="bg-purple-600 hover:bg-purple-700 text-white font-medium px-4 py-2 text-sm">
                {t("unlimitedGeneration")}
              </Badge>
            </div>
          </div>
        </div>
      </section>

      {/* AI Image Generation Section */}
      <section id="ai-generator" className="w-full py-12 md:py-24 bg-[#1a1310]">
        <div className="container mx-auto px-4 md:px-6">
          <div className="mx-auto max-w-3xl space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold">{t("aiGeneratorTitle")}</h2>
              <Badge variant="outline" className="bg-amber-500/10 text-amber-500 border-amber-500/20">
                {t("freeTrial")}
              </Badge>
            </div>
            <ImagePromptForm selectedPrompt={selectedPrompt} onPromptChange={setSelectedPrompt} />
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="w-full py-12 md:py-24 bg-[#1a1310]">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex flex-col items-center justify-center space-y-4 text-center">
            <h2 className="text-3xl font-bold">{t("inspiration")}</h2>
            <p className="text-muted-foreground">{t("inspirationDesc")}</p>
          </div>
          <div className="mt-8">
            <ImageGallery onImageClick={handleImageClick} />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <Features />

      {/* Stats Section */}
      <Stats />

      {/* Testimonials */}
      <Testimonials />

      {/* FAQ Section */}
      <div id="faq">
        <FAQ />
      </div>
    </div>
  )
}
